import { Injectable } from '@angular/core';
import { Features } from '../../../types/features';
import { User } from '../../../types/user/user';
import { JwtPayload } from '../../../services/tokenKeeper';
import {
  Route,
  RouterLinks,
} from '../../../services/application-routes.service';
import { isSSO } from '../../../utils/account';
import { insertIf } from '../../../utils';

// I would contend that putting this preference of cards vs. list view into the URL is misguided. It's
// reasonable if some other pages needed to specify one vs. the other, but they don't. Instead it's something
// which could easily be remembered in a cookie or localStorage and the page adjusts to show one vs. the other
// as needed. It shouldn't appear in the URLs.
const view = 'cards';

@Injectable({
  providedIn: 'root',
})

/*
 * FIXME: this duplication has become problematic.
 * We need to share the left and right menu configurations across frameworks to avoid duplication.
 * */
export class AptitudeNavService {
  constructor() {}

  // Note: After playing with this for a little while, my current solution is to allow for tests at the item level
  // and the menu item level below that. An item without a test is automatically included in the Nav, one with a
  // test is only included if the test returns true.

  getLeftItems(account: User, featureList: Features, tokenPayload: JwtPayload) {
    const isBuyer = () => account && account.isBuyer;
    const isGPO = () =>
      (account && account.isGPO) || tokenPayload.isNonAptitudeUser();

    // The existing code for this is a pooch *(%& of combining two different internal data structures, some test
    // code, and then a bunch more tests and flags only applied when the menu is constructed in the HTML. That makes
    // no damn sense.
    //
    // Construct it in one place and leave the code as easy to follow as you can. Every menu item is right here and
    // every test for every menu item is *right here*.

    if (isBuyer() || isGPO()) {
      return [
        {
          rule: true,
          header: 'aptitude marketplace',
        },
        {
          url: '/aptitude/static/platform/home/landing',
          text: 'Home',
        },
        {
          url: '/aptitude/static/platform/home/list/BUYER_REQUEST',
          text: 'Create',
          subUrls: [
            {
              url: '/aptitude/static/platform/home/list/BUYER_REQUEST',
              text: 'Categories',
            },
            {
              url: `/aptitude/static/platform/rfp/create?buyerId=${account.organization.id}`,
              text: 'Custom RFP',
            },
          ],
        },
        {
          url: '/aptitude/static/home.html#/execute/view/list/filter/activeOpportunities',
          text: 'Execute',
          subUrls: [
            {
              url: '/aptitude/static/home.html#/execute/view/list/filter/inflight',
              text: 'Inflight',
            },
            {
              url: '/aptitude/static/home.html#/execute/view/list/filter/sio',
              text: 'Seller Initiated Offers',
            },
            {
              url: '/aptitude/static/home.html#/execute/view/list/filter/joinGroupContract',
              text: 'Join Contracts',
              hasPermission: account.isPartner, // This should only display if it is a partner
            },
            {
              url: '/aptitude/static/home.html#/execute/view/list/filter/draftRfps',
              text: 'Draft RFPs',
            },
            {
              url: '/aptitude/static/home.html#/execute/view/list/filter/requestHistory',
              text: 'Request History',
            },
          ],
        },
        {
          text: 'Manage',
          routerLink: RouterLinks.MANAGE,
          queryParams: {},
          subUrls: [
            {
              text: 'Active Contracts',
              routerLink: RouterLinks.MANAGE,
              queryParams: { filter: 'active' },
            },
            {
              text: 'Expired Contracts',
              routerLink: RouterLinks.MANAGE,
              queryParams: { filter: 'expired' },
            },
          ],
        },
        {
          url: '/aptitude/static/platform/home/categories',
          text: 'Catalog',
        },
      ];
    } else {
      return [
        {
          rule: true,
          header: 'aptitude',
        },
        {
          url: '/aptitude/static/platform/home/landing',
          text: 'Home',
        },
        ...insertIf(featureList.OFFER_NEW_SIO_PAGE_PROD_OUTAGE, {
          routerLink: RouterLinks.RECOMMENDED_SELLER_INITAITED_OFFER,
          text: 'Offer',
        }),
        ...insertIf(!featureList.OFFER_NEW_SIO_PAGE_PROD_OUTAGE, {
          url: Route.OFFER_JS,
          text: 'Offer',
        }),
        {
          url: `/aptitude/static/platform${RouterLinks.SELLER_RESPOND}`,
          text: 'Respond',
        },
        {
          url: '/aptitude/static/home.html#/list/SELLER_ACCEPT',
          text: 'Execute',
        },
        {
          text: 'Manage',
          routerLink: RouterLinks.MANAGE,
          queryParams: {},
          subUrls: [
            {
              text: 'Active Contracts',
              routerLink: RouterLinks.MANAGE,
              queryParams: { filter: 'active' },
            },
            {
              text: 'Expired Contracts',
              routerLink: RouterLinks.MANAGE,
              queryParams: { filter: 'expired' },
            },
          ],
        },
        {
          routerLink: RouterLinks.CATALOG_CATEGORIES,
          text: 'Catalog',
        },
        {
          rule: false,
          header: 'company market activity',
        },
      ];
    }
  }

  getRightItems(
    account: User,
    featureList: Features,
    tokenPayload: JwtPayload,
  ) {
    const isBuyer = () => account && account.isBuyer;
    const isSeller = () => account && account.isSeller;
    const isGPO = () =>
      (account && account.isGPO) || tokenPayload.isNonAptitudeUser();

    return [
      {
        rule: true,
        header: 'company settings',
      },
      // Show the old page regardless
      {
        test: () => account.isSeller,
        url: '/aptitude/seller/SellerManagePricing',
        text: 'Catalog',
      },

      {
        routerLink: RouterLinks.MANAGE_USERS,
        text: 'Manage Users',
      },
      {
        test: () => (isBuyer() || isGPO()) && !account.isLeader,
        url: '/aptitude/buyer/EditBuyerProfile',
        text: 'Company Profile',
      },
      {
        test: () => isSeller() && !isGPO(),
        url: '/aptitude/seller/EditSellerProfile',
        text: 'Company Profile',
      },
      {
        test: () =>
          account.isBuyer &&
          (account.isPartner || account.isLeader) &&
          !isGPO(),
        url: '/aptitude/static/groupProfile.html',
        text: 'Group Profile',
      },
      {
        test: () => (isBuyer() || isGPO()) && !account.isLeader,
        url: '/aptitude/static/categoryPerformance.html',
        text: 'Target',
      },
      {
        test: () => isBuyer() || isGPO(),
        url: '/aptitude/static/agreements.html',
        text: 'Agreements',
      },
      {
        test: () => isSeller() && !isGPO(),
        url: '/aptitude/static/agreements.html',
        text: 'Agreements',
      },
      {
        test: () => isBuyer() || isGPO(),
        routerLink: '/organization/sellers',
        text: 'Sellers Directory',
      },
      {
        test: () => isSeller() && !isGPO(),
        routerLink: '/organization/buyers',
        text: 'Buyers Directory',
      },
      {
        rule: true,
        header: `my settings`,
      },
      {
        test: () => isBuyer() || isSeller() || isGPO(),
        url: '/aptitude/static/myAccount.html',
        text: 'My Account',
      },
      {
        test: () => account.numberOfOrgsUserBelongsTo > 1,
        routerLink: isSSO(account as { authenticationSource: string })
          ? RouterLinks.SIGN_IN_OKTA
          : RouterLinks.SIGN_IN_CHOOSE_ORGANIZATION,
        queryParams: isSSO(account as { authenticationSource: string })
          ? {}
          : { 'switch-accounts': true },
        text: 'Switch Accounts',
      },
      {
        click: () => {
          window.location.href = '/aptitude/static/platform/signIn/sign-out';
        },
        text: 'Sign Out',
      },
    ];
  }
}
